import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@staff-app/ui';

/** What a completed mint hands back for display. */
export interface MintedCoupon {
  code: string;
  /** Value the code was created for, when known. */
  amount?: number | null;
  /** The device it applies to, when the mint was per-device. */
  deviceName?: string | null;
  /** How many devices the code covers, when the mint was for a quantity. */
  quantity?: number | null;
  /**
   * Who it is for — email or Stripe customer id. OPTIONAL: a demo coupon records no recipient
   * (requester, 2026-08-24), so there is nobody to name and the sentence drops that clause
   * rather than printing an empty one.
   */
  customer?: string | null;
}

const money = (n: number) => `$${n.toFixed(2)}`;

/**
 * "Coupon created" confirmation.
 *
 * Rendered by the PAGE, not by the row's mint button, and that placement is load-bearing.
 * A successful mint gives the task a coupon, so the table immediately re-renders that device
 * row as "Issued" instead of a mint button — which unmounts the button and would destroy any
 * dialog state it owned before the dialog could ever appear. Hoisting it here makes the
 * confirmation outlive the row it came from.
 *
 * Dismissing returns to the queue, which by then already shows the coupon.
 */
export function MintedCouponDialog({
  minted,
  onClose,
  listed = false,
}: {
  minted: MintedCoupon | null;
  onClose: () => void;
  /**
   * True when the surface that opened this dialog also LISTS the code (the Demo Device Coupons
   * tab shows a Code column). It only changes the closing sentence, and it matters: telling
   * someone "this is the only copy" when the code is visible in the table below is wrong, and
   * the mistake pushes them into screenshotting a confirmation they did not need to.
   */
  listed?: boolean;
}) {
  return (
    <Dialog open={minted != null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent data-testid="order-minted-dialog">
        <DialogHeader>
          <DialogTitle>{'Coupon created ✅'}</DialogTitle>
          <DialogDescription>
            {minted
              ? `Single-use Shopify code${minted.customer ? ` for ${minted.customer}` : ''}${
                  minted.amount != null ? `, worth ${money(minted.amount)}` : ''
                }, attributed to you. Copy it and send it to the customer — ${
                  listed
                    ? 'it is also listed in the table below if you need it again.'
                    : 'it is the only copy shown here.'
                }`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3">
          <code className="flex-1 select-all font-mono text-lg tracking-wider" data-testid="order-minted-code">
            {minted?.code}
          </code>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => minted?.code && navigator.clipboard?.writeText(minted.code)}
          >
            {'Copy'}
          </Button>
        </div>
        {minted?.deviceName ? (
          <p className="text-xs text-muted-foreground">
            {`Applies to: ${minted.deviceName}${
              minted.quantity != null ? ` × ${minted.quantity}` : ''
            }`}
          </p>
        ) : null}
        <DialogFooter>
          <Button onClick={onClose} data-testid="order-minted-done">
            {'Back to Order Devices'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
